import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import tokenStore from '../services/tokenStore';
import { clearLocalSession } from '../utils/clearSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecureAuthContext = createContext(null);

export const useSecureAuth = () => {
  const context = useContext(SecureAuthContext);
  if (!context) {
    throw new Error('useSecureAuth must be used within SecureAuthProvider');
  }
  return context;
};

export const SecureAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Load user from localStorage on mount + bootstrap access token via the
  // httpOnly refresh cookie. The access token NEVER touches localStorage
  // (Phase 5 — XSS hardening complete).
  useEffect(() => {
    const storedUser = localStorage.getItem('maidly_auth_user');
    if (storedUser) setUser(JSON.parse(storedUser));

    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (r.ok) {
          const data = await r.json();
          if (data.access_token) {
            tokenStore.setAccessToken(data.access_token);
            setToken(data.access_token);
          }
        }
      } catch {
        /* offline or first load — keep cached user if any, no token yet */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Generate device ID for trusted device feature
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem('maidly_device_id');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('maidly_device_id', deviceId);
    }
    return deviceId;
  }, []);

  // Register new user
  const register = async (email, password, name, organizationName) => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true; // accept auth cookies from server

      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);

          if (xhr.status >= 200 && xhr.status < 300) {
            // Success - store auth data. Access token lives in memory + state;
            // refresh token lives in httpOnly cookie (server-set).
            setToken(data.access_token);
            setUser(data.user);
            tokenStore.setAccessToken(data.access_token);
            localStorage.setItem('maidly_auth_user', JSON.stringify(data.user));
            // Also populate the AuthContext store so ProtectedRoute (which
            // reads useAuth -> 'maidly_current_user') recognizes the freshly
            // registered user after a redirect. Without this, new signups
            // bounce to /login because the two auth contexts don't share state.
            localStorage.setItem('maidly_current_user', JSON.stringify(data.user));
            localStorage.setItem('maidly_last_activity', Date.now().toString());
            resolve({ success: true, user: data.user });
          } else {
            // Server returned an error
            resolve({ success: false, error: data.detail || 'Registration failed' });
          }
        } catch (parseError) {
          resolve({ success: false, error: 'Server returned an invalid response' });
        }
      };
      
      xhr.onerror = function() {
        resolve({ success: false, error: 'Network error. Please check your connection.' });
      };

      const payload = { email, password, name };
      // Only owners creating a business send organization_name — invited
      // employees omit it so the backend keeps them as org_role=null.
      if (organizationName && organizationName.trim()) {
        payload.organization_name = organizationName.trim();
      }
      xhr.send(JSON.stringify(payload));
    });
  };

  // Login user
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Check if 2FA is required
      if (data.requires_2fa) {
        setRequires2FA(true);
        setPendingEmail(email);
        return { 
          success: true, 
          requires2FA: true, 
          email,
          testCode: data.code_for_testing // For development only
        };
      }
      
      // Store auth data. Access token lives in memory + state;
      // refresh token lives in httpOnly cookie (server-set).
      setToken(data.access_token);
      setUser(data.user);
      tokenStore.setAccessToken(data.access_token);
      localStorage.setItem('maidly_auth_user', JSON.stringify(data.user));
      
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Verify 2FA code
  const verify2FA = async (code, rememberDevice = false) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingEmail,
          code,
          remember_device: rememberDevice,
          device_id: rememberDevice ? getDeviceId() : null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      // Store auth data. Access token lives in memory + state;
      // refresh token lives in httpOnly cookie (server-set).
      setToken(data.access_token);
      setUser(data.user);
      setRequires2FA(false);
      setPendingEmail(null);
      tokenStore.setAccessToken(data.access_token);
      localStorage.setItem('maidly_auth_user', JSON.stringify(data.user));
      
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Resend 2FA code
  const resend2FA = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-2fa?email=${encodeURIComponent(pendingEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }
      
      return { success: true, testCode: data.code_for_testing };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Forgot password - request reset code
  const forgotPassword = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Request failed');
      }
      
      return { success: true, testCode: data.code_for_testing };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Reset password with code
  const resetPassword = async (email, code, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Reset failed');
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Change password (for logged-in users)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: user.id,
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Password change failed');
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Toggle 2FA
  const toggle2FA = async (enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/toggle-2fa`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: user.id,
          enabled
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update 2FA settings');
      }
      
      // Update user state
      setUser(prev => ({ ...prev, two_factor_enabled: enabled }));
      
      return { 
        success: true, 
        recoveryCodes: data.recovery_codes || []
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Get security logs
  const getSecurityLogs = async () => {
    // Employees who signed in via the regular AuthContext don't populate
    // SecureAuthContext, so ``user`` can be null here. Bail early instead
    // of throwing ``Cannot read properties of null (reading 'id')`` —
    // that error was crashing the /security page into a blank screen.
    if (!user?.id) {
      return [];
    }
    try {
      const response = await fetch(`${API_URL}/api/auth/security-logs/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      return data.logs || [];
    } catch (error) {
      console.error('Error fetching security logs:', error);
      return [];
    }
  };

  // Logout
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setRequires2FA(false);
    setPendingEmail(null);
    tokenStore.clearAccessToken();
    // Full teardown (shared with AuthContext) — clears both user keys, the
    // workspace pointer and cached business data, and disconnects realtime.
    clearLocalSession();
    // Best-effort tell the server to clear the httpOnly refresh cookie too
    fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => { /* offline or rate-limited — cookie expires anyway */ });
  }, []);

  // Auto-logout on inactivity (15 minutes) — respects the user's
  // Settings → Security preference (e.g. GPS Fleet operators disable it).
  useEffect(() => {
    if (!user) return;

    const isEnabled = () => {
      try {
        const raw = localStorage.getItem('maidly_settings');
        if (!raw) return true;
        return JSON.parse(raw)?.security?.autoLogout !== false;
      } catch { return true; }
    };

    let timeout;
    let enabled = isEnabled();
    const resetTimeout = () => {
      clearTimeout(timeout);
      if (!enabled) return;
      timeout = setTimeout(() => {
        toast.info('Session expired due to inactivity');
        logout();
      }, 15 * 60 * 1000); // 15 minutes
    };
    const applyPreferenceChange = () => {
      enabled = isEnabled();
      resetTimeout();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetTimeout));
    window.addEventListener('maidly:settings-updated', applyPreferenceChange);
    window.addEventListener('storage', applyPreferenceChange);
    resetTimeout();

    return () => {
      clearTimeout(timeout);
      events.forEach(event => window.removeEventListener(event, resetTimeout));
      window.removeEventListener('maidly:settings-updated', applyPreferenceChange);
      window.removeEventListener('storage', applyPreferenceChange);
    };
  }, [user, logout]);

  const value = {
    user,
    token,
    loading,
    requires2FA,
    pendingEmail,
    isAuthenticated: !!user && !!token,
    register,
    login,
    verify2FA,
    resend2FA,
    forgotPassword,
    resetPassword,
    changePassword,
    toggle2FA,
    getSecurityLogs,
    logout
  };

  return (
    <SecureAuthContext.Provider value={value}>
      {children}
    </SecureAuthContext.Provider>
  );
};

export default SecureAuthContext;
